import type { CompanyFilter } from '../lib/companyFilter.ts';
import { SearchableMultiSelect } from './SearchableMultiSelect.tsx';

export type FilterBarOptions = {
  contractorTypes: string[];
  clusterRegions: string[];
  stations: string[];
};

/**
 * Filter bar for the admin Dashboard (its single consumer). Renders three
 * searchable multi-select dropdowns (contractor type, cluster region, station)
 * plus a "Clear all filters" button. State is fully controlled — the parent
 * owns the CompanyFilter value and passes onChange. `extraActive`/`onClearExtra`
 * let the parent hang one extra dimension (the Dashboard's free-text query) off
 * the same clear control, so the visible "filters are active" affordance covers
 * every dimension.
 */
export function FilterBar({
  options,
  value,
  onChange,
  extraActive = false,
  onClearExtra,
}: {
  options: FilterBarOptions;
  value: CompanyFilter;
  onChange: (next: CompanyFilter) => void;
  extraActive?: boolean;
  onClearExtra?: () => void;
}) {
  const hasAny =
    value.contractorTypes.length > 0 ||
    value.clusterRegions.length > 0 ||
    value.stations.length > 0 ||
    extraActive;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <SearchableMultiSelect
        label="Contractor type"
        unitLabel="contractor types"
        values={options.contractorTypes}
        selected={value.contractorTypes}
        onChange={(next) => onChange({ ...value, contractorTypes: next })}
      />
      <SearchableMultiSelect
        label="Cluster region"
        unitLabel="cluster regions"
        values={options.clusterRegions}
        selected={value.clusterRegions}
        onChange={(next) => onChange({ ...value, clusterRegions: next })}
      />
      <SearchableMultiSelect
        label="Station"
        unitLabel="stations"
        values={options.stations}
        selected={value.stations}
        onChange={(next) => onChange({ ...value, stations: next })}
      />
      {/* Always mounted so its width is reserved on sm+: the Dashboard's
          search box is flex-1 beside this bar, and mounting the button on the
          first keystroke used to shrink the box while typing. `invisible`
          also drops it from the tab order and accessibility tree; on narrow
          (stacked) viewports it is display:none so it holds no blank line. */}
      <button
        type="button"
        className={`rounded-md px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700 ${hasAny ? '' : 'hidden sm:invisible sm:inline-block'}`}
        onClick={() => {
          onChange({ contractorTypes: [], clusterRegions: [], stations: [] });
          onClearExtra?.();
        }}
      >
        Clear all filters
      </button>
    </div>
  );
}
